import Badge from '@material-ui/core/Badge'
import { makeStyles, styled, ThemeProvider } from '@material-ui/styles'
import clsx from 'clsx'
import moment from 'moment'
import { equals, prop, repeat } from 'ramda'
import {
  type FC,
  type PropsWithChildren,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { createPortal } from 'react-dom'
import { useSelector } from 'react-redux'
import type { RootState } from 'app/store'
import { type CacheState, cacheStoreKey, lastRefreshTimeStoreKey } from 'core/caching/cacheReducers'
import FontAwesomeIcon from 'core/components/FontAwesomeIcon'
import ServerSideSort, {
  type SortFieldOption,
} from 'core/components/server-side-pagination/ServerSideSort'
import IconButton from 'core/elements/button/IconButton'
import Text from 'core/elements/Text'
import Tooltip from 'core/elements/tooltip'
import useToggler from 'core/hooks/useToggler'
import type Theme from 'core/themes/model'
import { useCustomTheme } from 'core/themes/ThemeManager'
import type DataKeys from 'k8s/DataKeys'
import { ensureArray } from 'utils/fp'
import generateTestId from 'utils/test-helpers'
import type { Align } from '../tooltip/Tooltip'
import { BatchActionsContent } from './BatchActionsContent'
import GridColumnsPopover from './GridColumnsPopover'
import GridFilterRowContext from './GridFilterRowContext'
import type { GridFilteringProps } from './hooks/useGridFiltering'
import type { GridManagedColumnsProps } from './hooks/useGridManagedColumns'
import type {
  GridBatchActionGroupProps,
  GridBatchActionsProps,
} from './hooks/useGridSelectableRows'

interface GridToolbarProps<
  T,
  GF extends Record<string, unknown>,
  F extends Record<string, unknown>,
  DF extends Record<string, unknown>,
> extends GridFilteringProps<GF, F, DF>,
    GridBatchActionsProps<T>,
    GridManagedColumnsProps {
  compact?: boolean
  label?: string
  onRefresh?: () => void | Promise<void>
  extraToolbarContent?: ReactNode
  ToolbarContainer?: FC<PropsWithChildren<{ className?: string; selectedCount?: number }>>
  showItemsCountInLabel?: boolean
  itemsCount?: number
  tooltip?: ReactNode
  align?: Align
  isReadOnly?: boolean
  isBackgroundRefresh?: boolean
  dataKey?: DataKeys
  useFloatingActionBar?: boolean
  onManageColumnsToggleReady?: (toggle: () => void) => void
  searchComponent?: ReactNode
  sortOptions?: SortFieldOption[]
  sortedBy?: string
  sortedDirection?: 'asc' | 'desc'
  onToolbarSort?: (key: string, dir: 'asc' | 'desc') => void
  hideLastUpdatedTimeStamp?: boolean
}

const useStyles = makeStyles<Theme>((theme) => ({
  gridToolbar: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing(2),
    color: theme.components.table.toolbarColor,
    backgroundColor: theme.components.table.toolbar,
    transitionTimingFunction: 'ease-in',
    transition: 'background-color .2s ease',
    border: 0,
    borderRadius: '4px 4px 0 0',
    padding: '8px 16px 8px 16px',
    minHeight: 56,
    boxSizing: 'border-box',
    '&:last-child td': {
      borderBottom: 0,
    },
  },
  searchContainer: {
    display: 'flex',
    alignItems: 'center',
  },
  optionsContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginLeft: 'auto',
  },
  label: {
    ...theme.typography.subtitle2,
    color: 'inherit',
    marginRight: theme.spacing(3),
  },
  selectedCount: {
    color: 'inherit',
  },
  clearBtn: {
    cursor: 'pointer',
    color: theme.components.table.toolbarPassiveColor,
  },
  verticalLine: {
    borderLeft: `1px solid ${theme.components.typography.default}`,
    width: 1,
    height: 34,
    margin: theme.spacing(0, 1),
  },
  batchActions: {
    display: 'grid',
    gridAutoFlow: 'column',
    alignItems: 'center',
    justifyContent: 'start',
    gap: 12,
  },
  dropdownBatchActions: {
    display: 'grid',
    gridAutoFlow: 'column',
    alignItems: 'center',
    gap: 16,
    marginRight: 32,
  },
  tools: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  buttons: {
    display: 'grid',
    gridAutoFlow: 'column',
    marginRight: theme.spacing(1),
  },
  button: {
    ...theme.typography.inputTable,
    display: 'grid',
    gridAutoFlow: 'column',
    cursor: 'pointer',
    alignItems: 'center',
    padding: theme.spacing(1, 0),
    borderRadius: 4,
    border: `1px solid ${theme.components.table.border}`,
    gap: 8,
    '&:hover': {
      backgroundColor: theme.components.table.hoverBackground,
    },
  },
  extraContent: {
    marginLeft: theme.spacing(1),
  },
  tooltip: {
    display: 'inline-block',
    marginLeft: 8,
  },
  filterToggle: {
    width: 36,
    height: 36,
  },
  refreshIcon: {
    color: theme.components.table.toolbarColor,
    marginLeft: theme.spacing(1.5),
    fontWeight: 500,
  },
  actionDropdown: {
    position: 'relative',
    height: 36,
  },
  dropdownLabel: {
    display: 'inline-grid',
    gap: 8,
    gridTemplateColumns: 'repeat(3, max-content)',
    alignItems: 'center',
    cursor: 'pointer',
  },
  additionalDropdownLabelsStyles: {
    padding: '8px 12px',
    border: `solid 1px ${theme.palette.grey[200]}`,
    borderRadius: 4,
    background: theme.components.dropdown.background,
    boxSizing: 'border-box',
    height: '36px',
  },
  dropdownMenu: {
    display: 'grid',
    background: theme.components.dropdown.background, // Bc toolbar uses opposite mode, use the opposite colors
    zIndex: 1000,
    borderRadius: 4,
    border: `solid 1px ${theme.components.dropdown.border}`,
    overflowY: 'auto',
  },
  filterRowContainer: {
    background: theme.components.table.hoverBackground,
    '&:not(:empty)': {
      borderTop: `1px solid ${theme.components.table.border}`,
    },
  },
  filterBar: {
    transition: 'height .2s ease',
    height: 'max-content',
    overflow: 'visible',
    padding: '16px',
    background: theme.components.table.hoverBackground,
    display: 'grid',
    gap: 16,
    borderTop: `1px solid ${theme.components.table.border}`,
  },
  filterBarClosed: {
    height: 0,
    overflow: 'hidden',
    padding: '0px',
    borderTop: 'none',
  },
  filterDropdowns: {
    display: 'flex',
    gap: 16,
    alignItems: 'center',
    justifyContent: 'left',
    flexWrap: 'wrap',
    width: '100%',
    '& > div': {
      flex: '0 1',
      flexBasis: '18.5%',
    },
  },
  activeFilters: {
    display: 'flex',
    gap: 16,
    flexWrap: 'wrap',
  },
  activeFilterBox: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, max-content)',
    gap: 8,
    alignItems: 'center',
    padding: '4px 12px',
    background: theme.components.table.background,
    border: `1px solid ${theme.components.table.border}`,
  },
  activeFilterText: {
    fontSize: 12,
  },
  withIcon: {
    display: 'grid',
    gridTemplateColumns: '16px auto',
    gap: 12,
    alignItems: 'center',
  },
  strong: {
    fontWeight: 800,
  },
  lastUpdatedText: {
    display: 'inline',
    marginLeft: 12,
  },
  batchActionWrapper: {
    cursor: 'pointer',
  },
  batchActionWrapperDisabled: {
    cursor: 'not-allowed',
    opacity: 0.6,
  },
}))

const DefaultToolbarContainer: FC<
  PropsWithChildren<{
    selectedCount?: number
    className?: string
  }>
> = ({ children, selectedCount, ...props }) => {
  const [theme] = useCustomTheme()
  return (
    <ThemeProvider theme={theme}>
      <div {...props}>{children}</div>
    </ThemeProvider>
  )
}

// Consider moving to a helper file
// Detecting clicks outside the dropdown (and any of its extraRefs, e.g. a portaled menu)
const clickOutside = (ref, onClickOutside, extraRefs = []) => {
  useEffect(() => {
    const modalRef = document.getElementById('modal-portal-root')
    const handleClickOutside = (event) => {
      const isInsideExtraRef = extraRefs.some((extraRef) =>
        extraRef.current?.contains(event.target),
      )
      if (
        ref.current &&
        !ref.current.contains(event.target) &&
        !isInsideExtraRef &&
        !modalRef.contains(event.target) &&
        !document.body.classList.contains('blur') // Added this check to resolve https://platform9.atlassian.net/browse/PCD-4153
      ) {
        onClickOutside()
      }
    }
    // Bind
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      // dispose
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [ref, onClickOutside, extraRefs])
}

const getPortalRoot = () => document.getElementById('row-menu-portal-root') || document.body

export function BatchActionDropdown<T>({
  actions,
  icon,
  label = 'Other Actions',
  renderOutsideGrid = false,
  styledButton = false,
}: GridBatchActionGroupProps<T>) {
  const classes = useStyles({})
  const [isOpen, setOpen] = useState(false)
  const [menuPosition, setMenuPosition] = useState<{
    top?: number
    bottom?: number
    right: number
    maxHeight?: number
  }>(null)

  const wrapperRef = useRef(null)
  const menuRef = useRef(null)
  const handleClickOutside = useCallback(() => setOpen(false), [])
  const extraRefs = useMemo(() => [menuRef], [])
  clickOutside(wrapperRef, handleClickOutside, extraRefs)

  const applyStyling = renderOutsideGrid || styledButton

  const computeMenuPosition = useCallback(() => {
    const rect = wrapperRef.current?.getBoundingClientRect()
    if (!rect) return
    const right = window.innerWidth - rect.right
    const margin = 8
    setMenuPosition(
      renderOutsideGrid
        ? { bottom: window.innerHeight - rect.top, right, maxHeight: rect.top - margin }
        : {
            top: rect.bottom,
            right,
            maxHeight: window.innerHeight - rect.bottom - margin,
          },
    )
  }, [renderOutsideGrid])

  const toggleOpen = () => {
    if (!isOpen) {
      computeMenuPosition()
    }
    setOpen(!isOpen)
  }

  // Anchor (wrapperRef) can move while the menu is open — e.g. this bar
  // transitioning in/out of "stuck" state, or the page scrolling/resizing.
  // Recompute the portal's fixed position so it stays attached to its anchor.
  useEffect(() => {
    if (!isOpen) return undefined
    computeMenuPosition()
    window.addEventListener('scroll', computeMenuPosition, true)
    window.addEventListener('resize', computeMenuPosition)
    return () => {
      window.removeEventListener('scroll', computeMenuPosition, true)
      window.removeEventListener('resize', computeMenuPosition)
    }
  }, [isOpen, computeMenuPosition])

  return (
    <div className={classes.actionDropdown} ref={wrapperRef}>
      <div
        className={clsx(
          classes.dropdownLabel,
          applyStyling && classes.additionalDropdownLabelsStyles,
        )}
        onClick={toggleOpen}
      >
        <FontAwesomeIcon>{icon}</FontAwesomeIcon>
        <Text variant={'body2'} className={clsx(applyStyling && classes.strong)}>
          {label}
        </Text>
        <FontAwesomeIcon solid size="sm">
          {isOpen ? 'caret-up' : 'caret-down'}
        </FontAwesomeIcon>
      </div>
      {/* Once opened, keep this mounted (just hidden) instead of unmounting on
          close — an action's BatchActionButton (e.g. getGridDialogButton) can
          open a dialog and close the dropdown in the same click; unmounting
          here would tear the dialog down with it. */}
      {(isOpen || menuPosition) &&
        createPortal(
          <div
            ref={menuRef}
            className={classes.dropdownMenu}
            style={{ position: 'fixed', ...menuPosition, display: isOpen ? undefined : 'none' }}
          >
            {actions?.map(
              ({
                key,
                label,
                triggerAction,
                tooltip,
                align,
                BatchActionButton,
                icon,
                disabled,
                hideIfDisabled,
                ...props
              }) => {
                if (disabled && hideIfDisabled) {
                  return null
                }

                return (
                  <BatchActionButton
                    {...props}
                    key={key}
                    disabled={disabled}
                    onClick={() => {
                      triggerAction()
                      setOpen(false)
                    }}
                    closeDropdown={() => setOpen(false)}
                  >
                    <Tooltip className={classes.tooltip} message={tooltip || ''} align={align}>
                      <div className={classes.withIcon}>
                        {icon && <FontAwesomeIcon noBaseClass>{icon}</FontAwesomeIcon>}
                        {label}
                      </div>
                    </Tooltip>
                  </BatchActionButton>
                )
              },
            )}
          </div>,
          getPortalRoot(),
        )}
    </div>
  )
}

const GridRefreshButton = styled(({ className, onRefresh, iconOnly }) => {
  return (
    <Text
      data-testid={generateTestId('refresh')}
      noWrap
      onClick={onRefresh}
      component="div"
      className={className}
    >
      <FontAwesomeIcon>sync-alt</FontAwesomeIcon>
      {!iconOnly && 'Refresh'}
    </Text>
  )
})<Theme>(({ theme }) => ({
  ...theme.typography.inputTable,
  display: 'grid',
  gridAutoFlow: 'column',
  cursor: 'pointer',
  alignItems: 'center',
  padding: theme.spacing(2, 1),
  borderRadius: 4,
  border: `1px solid ${theme.components.table.border}`,
  gap: 8,
  '&:hover': {
    backgroundColor: theme.components.table.hoverBackground,
  },
}))

export default function GridToolbar<
  T,
  GF extends Record<string, unknown>,
  F extends Record<string, unknown>,
  DF extends Record<string, unknown>,
>(props: GridToolbarProps<T, GF, F, DF>) {
  const [isOpen, toggleIsOpen] = useToggler(false)
  const [manageColumnsOpen, toggleManageColumns] = useToggler(false)
  const [filterRowContainerEl, setFilterRowContainerEl] = useState<HTMLDivElement | null>(null)
  const classes = useStyles()
  const {
    label,
    columns,
    columnTogglers,
    selectedCount,
    batchActions,
    dropdownBatchActions,
    globalFilters,
    filters,
    dropdownFilters,
    dropdownFilterValues,
    dropdownValuesByKey,
    onRefresh,
    extraToolbarContent,
    clearSelectedRows,
    multiSelectionEnabled,
    ToolbarContainer = DefaultToolbarContainer,
    columnHidingDisabled,
    showItemsCountInLabel = true,
    itemsCount = undefined,
    tooltip = undefined,
    isReadOnly,
    isBackgroundRefresh = false,
    dataKey,
    useFloatingActionBar = true,
    onManageColumnsToggleReady,
    searchComponent,
    sortOptions,
    sortedBy,
    sortedDirection,
    onToolbarSort,
    hideLastUpdatedTimeStamp = false,
  } = props

  const removeFilterValue = useCallback(
    (filterValue) => {
      const keyValues = ensureArray(dropdownValuesByKey[filterValue.key])
      const filteredKeyValues = keyValues.filter((val) => {
        return !equals(val, filterValue.value)
      })
      filterValue.updateFilterValue(filteredKeyValues)
    },
    [dropdownValuesByKey],
  )

  useEffect(() => {
    if (onManageColumnsToggleReady) {
      onManageColumnsToggleReady(toggleManageColumns)
    }
  }, [onManageColumnsToggleReady, toggleManageColumns])
  const cache = useSelector<RootState, CacheState>(prop(cacheStoreKey))

  const timestamp = useMemo(() => {
    const cacheTimestamp = cache?.[lastRefreshTimeStoreKey]?.[dataKey]
    if (!dataKey || !cacheTimestamp) {
      return ''
    }
    const timeChecked = moment(cacheTimestamp).format('h:mm:ss A')
    return timeChecked
  }, [dataKey, cache[lastRefreshTimeStoreKey]])

  // We need to hide search and sort options to make room for Batch Ations
  // when useFloatingActionBar=false and rows are selected
  const hideSearchAndSortOptions = !useFloatingActionBar && selectedCount > 0
  const showSearchAndSortOptions = useMemo(
    () =>
      (searchComponent || globalFilters.length > 0 || filters.length > 0) &&
      !hideSearchAndSortOptions,
    [searchComponent, globalFilters, filters, hideSearchAndSortOptions],
  )

  return (
    <GridFilterRowContext.Provider value={filterRowContainerEl}>
      <ToolbarContainer selectedCount={selectedCount} className={classes.gridToolbar}>
        <div className={classes.batchActions}>
          <Text
            data-testid={generateTestId(label, 'label')}
            className={classes.label}
            variant="subtitle2"
            component="p"
          >
            {showItemsCountInLabel && itemsCount ? `${label} (${itemsCount})` : label}
            {tooltip && (
              <Tooltip className={classes.tooltip} message={tooltip}>
                <FontAwesomeIcon>info-circle</FontAwesomeIcon>
              </Tooltip>
            )}
            {isBackgroundRefresh && (
              <Tooltip className={classes.tooltip} message="Refreshing">
                <FontAwesomeIcon className={classes.refreshIcon} spin size="lg">
                  refresh
                </FontAwesomeIcon>
              </Tooltip>
            )}
            {!hideLastUpdatedTimeStamp && timestamp && (
              <Text variant="body2" className={classes.lastUpdatedText}>
                Last Updated {timestamp}
              </Text>
            )}
          </Text>
          {!useFloatingActionBar && selectedCount ? (
            <>
              {multiSelectionEnabled ? (
                <>
                  <Text
                    data-testid={generateTestId('selected')}
                    variant="body2"
                    className={classes.selectedCount}
                    component="p"
                  >
                    {`${selectedCount} Selected`}
                  </Text>
                  <Text
                    data-testid={generateTestId('clear', 'all')}
                    variant="body2"
                    className={classes.clearBtn}
                    component="p"
                    onClick={clearSelectedRows}
                  >
                    {`Clear All`}
                  </Text>
                  <div className={classes.verticalLine} />
                </>
              ) : null}
              {!isReadOnly && (
                <BatchActionsContent<T>
                  batchActions={batchActions}
                  dropdownBatchActions={dropdownBatchActions}
                  actionWrapperClassName={classes.batchActionWrapper}
                  actionWrapperDisabledClassName={classes.batchActionWrapperDisabled}
                  dropdownContainerClassName={classes.dropdownBatchActions}
                />
              )}
            </>
          ) : null}
        </div>
        {showSearchAndSortOptions && (
          <div className={classes.optionsContainer}>
            {searchComponent && <div className={classes.searchContainer}>{searchComponent}</div>}
            <div data-testid={generateTestId('search')} className={classes.tools}>
              {globalFilters.map(
                ({
                  key,
                  label,
                  filterValue,
                  filterValues,
                  updateFilterValue,
                  FilterComponent,
                  ...rest
                }) => (
                  <FilterComponent
                    key={String(key)}
                    value={filterValue}
                    filterValues={filterValues}
                    onChange={updateFilterValue}
                    label={label}
                    {...rest}
                  />
                ),
              )}
              {!!dropdownFilters?.length && (
                <Badge badgeContent={!isOpen ? dropdownFilterValues?.length : 0} color="primary">
                  <IconButton
                    className={classes.filterToggle}
                    onClick={toggleIsOpen}
                    icon="filter"
                    info={isOpen ? 'Hide Filters' : 'Show Filters'}
                  />
                </Badge>
              )}
              {sortOptions?.length > 0 && onToolbarSort && (
                <ServerSideSort
                  sortOptions={sortOptions}
                  onSort={onToolbarSort}
                  sortKey={sortedBy}
                  sortDir={sortedDirection}
                />
              )}
              {!columnHidingDisabled && (
                <Tooltip message="Manage Columns" align={{ vertical: 'top', horizontal: 'middle' }}>
                  <GridColumnsPopover
                    columns={columns}
                    columnTogglers={columnTogglers}
                    isOpen={manageColumnsOpen}
                    onToggle={toggleManageColumns}
                    iconOnly
                  />
                </Tooltip>
              )}
              {onRefresh && (
                <Tooltip message="Refresh" align={{ vertical: 'top', horizontal: 'middle' }}>
                  <GridRefreshButton iconOnly onRefresh={onRefresh} />
                </Tooltip>
              )}

              {filters.map(
                ({
                  key,
                  filterValue,
                  filterValues,
                  updateFilterValue,
                  FilterComponent,
                  ...rest
                }) => (
                  <FilterComponent
                    key={String(key)}
                    value={filterValue}
                    filterValues={filterValues}
                    onChange={updateFilterValue}
                    {...rest}
                  />
                ),
              )}
              {extraToolbarContent ? (
                <div className={classes.extraContent}>{extraToolbarContent}</div>
              ) : null}
            </div>
          </div>
        )}
      </ToolbarContainer>
      <div
        className={clsx(classes.filterBar, {
          [classes.filterBarClosed]: !isOpen,
        })}
      >
        <div className={clsx(classes.filterDropdowns)}>
          {dropdownFilters?.map(
            ({ key, filterValue, updateFilterValue, FilterComponent, filterComponentProps }) => (
              <FilterComponent
                key={key}
                value={filterValue}
                onChange={updateFilterValue}
                {...filterComponentProps}
              />
            ),
          )}
        </div>
        {!!dropdownFilterValues?.length && (
          <div className={classes.activeFilters}>
            {dropdownFilterValues?.map((filterInfo) => (
              <div key={filterInfo?.display} className={classes.activeFilterBox}>
                <Text variant="body2" className={classes.activeFilterText}>
                  <b>{filterInfo?.label}:</b> {filterInfo?.display}
                </Text>
                <FontAwesomeIcon
                  onClick={() => {
                    removeFilterValue(filterInfo)
                  }}
                  size="md"
                >
                  xmark
                </FontAwesomeIcon>
              </div>
            ))}
          </div>
        )}
      </div>
      <div ref={setFilterRowContainerEl} className={classes.filterRowContainer} />
    </GridFilterRowContext.Provider>
  )
}
